// Phase 40 — Smart Voice Order parsing.
//
// frontend_Admin's own Voice Order (components/VoiceOrder.jsx) sends the raw
// transcript to a backend AI parser, POST /api/voice/parse
// (backend/routers/voice.py). That entire router is mounted
// `APIRouter(dependencies=[Depends(staff_only)])` — every route on it,
// /parse included, is staff-only. frontend_user's visitors are guests or the
// "customer" role, never staff, so calling it would 401/403 as-is, and
// making it reachable from here would mean loosening a staff_only gate —
// an RBAC architecture change, which this phase explicitly forbids ("DO NOT
// modify RBAC architecture") and which Section 44 says to stop and report
// rather than do silently. A backend change is NOT required for the actual
// objective (Product + Unit + Quantity -> confirm -> cart) though: this file
// is a small, self-contained equivalent that matches against the SAME real
// product/unit data frontend_user already fetches from the existing public
// GET /products and GET /products/{id}/units endpoints — no new API, no
// invented per-product aliases, no hard-coded product/unit IDs.
//
// Pure functions only (no DOM/React, no network) so they're trivially
// testable with a plain Node script — see the phase's own test matrix.
// Product matching only needs product NAMES; unit matching needs the
// specific matched product's OWN units, which live behind a per-product API
// call — so this is split into two steps (matchProductAndQuantity, then
// resolveUnitAndFinalize once that one product's units are known) rather
// than requiring every product's units to be pre-fetched up front.

// ── Quantity extraction ─────────────────────────────────────────────────────

const EN_NUMBER_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
  nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
  fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
  twenty: 20,
};

// Well-known, unambiguous Khmer digit script and a small set of common
// spoken number words — not a general Khmer NLP engine, just the bounded
// vocabulary needed to read a quantity out of a short spoken order.
const KHMER_DIGIT_MAP = { "០": "0", "១": "1", "២": "2", "៣": "3", "៤": "4", "៥": "5", "៦": "6", "៧": "7", "៨": "8", "៩": "9" };
const KM_ONES = { "មួយ": 1, "ពីរ": 2, "បី": 3, "បួន": 4, "ប្រាំ": 5, "ប្រាំមួយ": 6, "ប្រាំពីរ": 7, "ប្រាំបី": 8, "ប្រាំបួន": 9 };
const KM_TENS = { "ដប់": 10, "ម្ភៃ": 20 };
// Compound numbers (11-19, 21-29) are built deterministically by
// concatenating a tens word with a ones word — this is literally how these
// words are formed in Khmer (ដប់ + មួយ = ដប់មួយ = 11), not a guess or a
// broader NLP system. Bounded to exactly the tens this shop's orders are
// realistically likely to need (Section 7 explicitly limits scope here).
const KM_NUMBER_WORDS = { ...KM_ONES, ...KM_TENS };
for (const [tensWord, tensValue] of Object.entries(KM_TENS)) {
  for (const [onesWord, onesValue] of Object.entries(KM_ONES)) {
    KM_NUMBER_WORDS[tensWord + onesWord] = tensValue + onesValue;
  }
}

function khmerDigitsToArabic(text) {
  return text.replace(/[០-៩]/g, (d) => KHMER_DIGIT_MAP[d] ?? d);
}

// Finds a quantity anywhere in the text and returns { qty, textWithoutQty }
// so the remaining words can be matched against the product name without the
// number itself being mistaken for part of it. Returns { qty: null, ... }
// when no valid quantity is present — callers must treat that as "ask the
// user for a quantity", never default to 1 silently (Section 20/11).
export function extractQuantity(rawText) {
  const text = khmerDigitsToArabic(rawText || "");

  // 1. A plain numeral, anywhere in the string. The trailing boundary
  // accepts common punctuation (,.;:) as well as whitespace — a phrase like
  // "Heineken, quantity 2, can" has "2" followed by a comma, and "Heineken
  // Can 3." ends with a period, neither of which a naive whitespace-only
  // boundary would catch. A trailing "." is only accepted when NOT followed
  // by another digit, so a real decimal quantity in a product's own name
  // (e.g. this shop's real "1.5L - ..." product) is never split into a
  // false "1" — the leading boundary stays whitespace-only, since a numeral
  // is never legitimately preceded by punctuation in any required example.
  const numMatch = text.match(/(?:^|\s)(\d+)(?:[\s,;:]|\.(?!\d)|$)/);
  if (numMatch) {
    const n = parseInt(numMatch[1], 10);
    const stripped = (text.slice(0, numMatch.index) + " " + text.slice(numMatch.index + numMatch[0].length)).trim();
    if (Number.isFinite(n) && n > 0) {
      return { qty: n, textWithoutQty: stripped };
    }
    // A numeral of 0 (or non-positive) was said — explicit invalid quantity,
    // not "no quantity given". Reported distinctly so the UI can say
    // "quantity must be greater than 0" rather than "please say a quantity".
    // The numeral is still stripped from the remaining text either way.
    return { qty: null, invalid: true, textWithoutQty: stripped };
  }

  // 2. An English number word — punctuation-aware trailing boundary only
  // (a number word is never legitimately preceded by punctuation in any
  // required example, and words have no decimal-number pitfall to guard
  // against, unlike numerals).
  const lower = text.toLowerCase();
  for (const [word, n] of Object.entries(EN_NUMBER_WORDS)) {
    const re = new RegExp(`(?:^|\\s)${word}(?:[\\s,.;:]|$)`, "i");
    const m = lower.match(re);
    if (m) {
      return { qty: n, textWithoutQty: (text.slice(0, m.index) + " " + text.slice(m.index + m[0].length)).trim() };
    }
  }

  // 3. A Khmer number word — longest words first so "ប្រាំមួយ" (6) isn't
  // partially matched by "ប្រាំ" (5) first.
  const kmWords = Object.keys(KM_NUMBER_WORDS).sort((a, b) => b.length - a.length);
  for (const word of kmWords) {
    const idx = text.indexOf(word);
    if (idx !== -1) {
      return { qty: KM_NUMBER_WORDS[word], textWithoutQty: (text.slice(0, idx) + " " + text.slice(idx + word.length)).trim() };
    }
  }

  return { qty: null, textWithoutQty: text };
}

// ── Text normalization ───────────────────────────────────────────────────
// .toLowerCase()/.trim() are no-ops on Khmer script (it has no case), so
// this is safe to apply unconditionally without corrupting Khmer product
// names — never strips/transliterates Khmer characters.
export function normalizeText(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    // Punctuation only — e.g. this shop's real product names use a
    // parenthesized packaging hint like "(កំប៉ុង) ហេននីហ្គេន" ("(Can) Heineken")
    // and a dash separator like "1.5L - ប្រូវីដា", neither of which anyone
    // actually speaks aloud literally. Stripping this formatting punctuation
    // is safe normalization; it never touches Khmer characters or removes
    // any word from the product's real identity.
    .replace(/[,.!?()[\]{}-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ── Filler words — spoken politeness/intent words that carry no product
// identity ───────────────────────────────────────────────────────────────
// Stripped ONLY from the spoken candidate text before scoring it against a
// product name — never from the stored product name itself, and never
// applied to unit matching (matchUnitFromText already only reacts to known
// packaging synonyms, so leftover filler words there are simply ignored).
// Deliberately excludes any packaging/unit word (can, bottle, កំប៉ុង, ដប,
// ...): this shop's real product names sometimes embed their own packaging
// hint in parentheses (e.g. "(កំប៉ុង) ហេននីហ្គេន"), so stripping "កំប៉ុង" here
// would destroy the exact signal that disambiguates it from
// "(ដប) ហេននីហ្គេន" — see Section 15's own explicit warning about this.
// Sorted longest-phrase-first so a multi-word filler ("give me") is
// recognized as a whole phrase before any of its individual words could be
// mistaken for something else.
const FILLER_PHRASES = [
  "give me", "i want", "i need", "please", "add", "buy", "order", "quantity",
  "units of", "unit of", "of",
  // Khmer ordering/intent words and connecting words (Phase 40.3, Sections
  // 5/14) — each one is a real, common way to phrase "please"/"I want"/
  // "add"/"of" in Khmer, never a product or unit word.
  "ខ្ញុំចង់បាន", "ចង់បាន", "ចង់យក", "ខ្ញុំយក", "សុំ", "សូម", "សូមយក", "យក", "ដាក់",
  "បន្ថែម", "សូមបន្ថែម", "ជួយបន្ថែម", "ចំនួន", "មានចំនួន",
  "ឲ្យខ្ញុំ", "អោយខ្ញុំ", "ផ្តល់", "បញ្ជាទិញ", "នៃ", "របស់",
  // "បង" — a polite address term ("older sibling/you", used the way
  // "hey"/"could you" is in a casual spoken request, e.g. "បងយកហេននីហ្គេន...")
  // — never a product or unit name, so it's as safe to strip as any other
  // intent word above (Phase 42).
  "បង",
].sort((a, b) => b.split(" ").length - a.split(" ").length || b.length - a.length);

// A spoken price ("...for $0.10" / "...តម្លៃ ០.១០") must never affect product
// matching (Section 13/19) — the marker word itself, and the price VALUE
// that follows it, are both dropped. The value is skipped as a short run of
// numeric-looking tokens (not just one) because normalizeText's own
// punctuation stripping splits "$0.10" into two tokens ("$0", "10") before
// this ever runs.
// Deliberately excludes the English word "price" itself — unlike "for"/"at"
// (function words that essentially never appear inside a real product
// name), "price" is common enough in ordinary product naming (e.g. this
// project's own real "Decimal Price Test" fixture product) that treating it
// as a universal marker risks destroying a real product's identity, which
// Section 5/14 explicitly warns against. Neither required example (Section
// 13) actually needs the bare word "price" to be recognized.
const PRICE_MARKER_WORDS = ["តម្លៃ", "for", "at"];

function stripPriceClause(words) {
  const kept = [];
  let i = 0;
  while (i < words.length) {
    if (PRICE_MARKER_WORDS.includes(words[i])) {
      i++;
      while (i < words.length && /^\$?\d/.test(words[i])) i++;
      continue;
    }
    kept.push(words[i]);
    i++;
  }
  return kept;
}

// Khmer script has no spaces between words at all in normal writing, and a
// SpeechRecognition transcript may come back the same way — e.g. the
// required "បងយកហេននីហ្គេន ២ កំប៉ុង" has "បង"+"យក"+"ហេននីហ្គេន" fused into one
// token with no boundary. Rather than attempting general Khmer word
// segmentation (a real, unbounded NLP problem — explicitly out of scope,
// Section 41), this only ever peels a KNOWN filler word off the FRONT of a
// token (repeatedly, since "បងយក..." has two stacked fillers) and a KNOWN
// unit synonym off the BACK of what's left — the two bounded vocabularies
// this parser already has reason to know about. Anything in between is
// left exactly as spoken; this never guesses at an unknown product name's
// internal boundaries.
function segmentKnownFillerPrefix(word, singleWordFillers) {
  let current = word;
  let changed = true;
  while (changed) {
    changed = false;
    for (const filler of singleWordFillers) {
      if (current !== filler && current.startsWith(filler) && current.length > filler.length) {
        current = current.slice(filler.length);
        changed = true;
        break;
      }
    }
  }
  return current;
}

function segmentKnownUnitSuffix(word) {
  const allUnitWords = Object.values(UNIT_SYNONYMS).flat().sort((a, b) => b.length - a.length);
  for (const unitWord of allUnitWords) {
    if (word !== unitWord && word.endsWith(unitWord) && word.length > unitWord.length) {
      return [word.slice(0, word.length - unitWord.length), unitWord];
    }
  }
  return [word];
}

function segmentCompoundTokens(words) {
  const singleWordFillers = FILLER_PHRASES.filter((p) => !p.includes(" ")).sort((a, b) => b.length - a.length);
  const out = [];
  for (const word of words) {
    const afterFillers = segmentKnownFillerPrefix(word, singleWordFillers);
    out.push(...segmentKnownUnitSuffix(afterFillers));
  }
  return out;
}

function stripPhraseList(words, phrases) {
  let current = words;
  for (const phrase of phrases) {
    const phraseWords = phrase.split(" ");
    const kept = [];
    let i = 0;
    while (i < current.length) {
      if (current.slice(i, i + phraseWords.length).join(" ") === phrase) {
        i += phraseWords.length; // drop the whole filler phrase
      } else {
        kept.push(current[i]);
        i++;
      }
    }
    current = kept;
  }
  return current;
}

// Combines both cleanup passes on the SPOKEN candidate text only, used
// exclusively for product-identity scoring — never applied to unit
// matching (which only reacts to known packaging synonyms) or to the
// stored product name itself.
function cleanForProductMatching(normalizedText) {
  const words = normalizedText.split(" ").filter(Boolean);
  const segmented = segmentCompoundTokens(words);
  return stripPhraseList(stripPriceClause(segmented), FILLER_PHRASES).join(" ").trim();
}

// ── Unit synonyms — spoken word -> normalized concept ───────────────────────
// A concept is matched against the ALREADY-MATCHED product's own real
// unit_code values (never a hard-coded word -> unit-ID table) — see
// resolveUnitConcept below. Extending this list only teaches the parser a
// new spoken synonym for an existing packaging concept; it never assigns a
// concept to a specific product.
const UNIT_SYNONYMS = {
  can: ["can", "cans", "កំប៉ុង"],
  bottle: ["bottle", "bottles", "ដប"],
  case: ["case", "cases", "កេស"],
  box: ["box", "boxes", "ប្រអប់"],
  piece: ["piece", "pieces", "pcs", "pc", "ដុំ"],
  cup: ["cup", "cups", "កែវ"],
  bag: ["bag", "bags", "ថង់"],
};

function conceptForWord(word) {
  const w = normalizeText(word);
  for (const [concept, synonyms] of Object.entries(UNIT_SYNONYMS)) {
    if (synonyms.includes(w)) return concept;
  }
  return null;
}

function conceptForUnitCode(unitCode) {
  return conceptForWord(unitCode);
}

// Finds a spoken packaging word anywhere in the text and resolves it to one
// of THIS PRODUCT'S OWN real active units, never a unit from another
// product. Returns null (not a guess) when no packaging word is spoken or
// none of the product's units match that concept.
export function matchUnitFromText(text, activeUnits) {
  const words = normalizeText(text).split(" ");
  let spokenConcept = null;
  for (const word of words) {
    const c = conceptForWord(word);
    if (c) { spokenConcept = c; break; }
  }
  if (!spokenConcept) return null;
  return activeUnits.find((u) => conceptForUnitCode(u.unit_code) === spokenConcept) || null;
}

// ── Product matching ─────────────────────────────────────────────────────
// Priority (Section 42): exact name > exact normalized name > containment >
// token-overlap fuzzy match. No SKU/barcode tier — ProductOut (the existing
// public product response frontend_user already consumes) carries no
// barcode field today, and adding one would mean a new API call this phase
// doesn't need. High-confidence auto-select only; anything else is
// surfaced to the user to choose, never guessed silently (Section 13/42).
function tokenOverlapScore(a, b) {
  const wordsA = new Set(normalizeText(a).split(" ").filter(Boolean));
  const wordsB = new Set(normalizeText(b).split(" ").filter(Boolean));
  if (!wordsA.size || !wordsB.size) return 0;
  let shared = 0;
  for (const w of wordsA) if (wordsB.has(w)) shared++;
  return shared / Math.max(wordsA.size, wordsB.size);
}

function sameWordSet(a, b) {
  const wordsA = new Set(a.split(" ").filter(Boolean));
  const wordsB = new Set(b.split(" ").filter(Boolean));
  if (!wordsA.size || wordsA.size !== wordsB.size) return false;
  for (const w of wordsA) if (!wordsB.has(w)) return false;
  return true;
}

function scoreAgainstName(norm, name) {
  if (!norm || !name) return 0;
  if (norm === name) return 1;
  if (name.includes(norm) || norm.includes(name)) return 0.85;
  // Same words, different order — e.g. spoken "ហេននីហ្គេន កំប៉ុង" against the
  // stored "(កំប៉ុង) ហេននីហ្គេន" (normalized "កំប៉ុង ហេននីហ្គេន"). This is a
  // complete match modulo word order, not a partial/fuzzy one, so it scores
  // above the plain token-overlap cap — this is what lets a spoken
  // packaging word disambiguate between two products that share a name but
  // differ in their own embedded packaging hint (Section 11).
  if (sameWordSet(norm, name)) return 0.9;
  return tokenOverlapScore(norm, name) * 0.8; // capped below the containment tier
}

// Phase 42 — Global Voice Training: a product may ALSO be recognized by any
// Admin-confirmed natural-sentence example trained for it (fetched once via
// the public GET /voice-training/active — see voiceTrainingService.js),
// not just its own stored name. Scored with the exact same tiers as the
// name itself (via scoreAgainstName) and combined with max() — a trained
// phrase never LOWERS a product's score, it only ever gives it another way
// to be recognized. This is why training memory needs no separate parser:
// it's just more candidate text fed through the one existing scorer.
function scoreProduct(candidateText, product, trainedPhrases) {
  // Filler- and price-clause-stripped on the SPOKEN side only — "give me 2
  // Heineken cans" should score exactly as well as "Heineken cans" once
  // "give me" (a politeness/intent phrase, never part of a real product's
  // identity) is removed, and "...for $0.10"/"...តម្លៃ ០.១០" should never
  // pollute the match either; the stored product name is compared as-is.
  const norm = cleanForProductMatching(normalizeText(candidateText));
  if (!norm) return 0;
  let best = scoreAgainstName(norm, normalizeText(product.name));
  for (const phrase of trainedPhrases || []) {
    // A trained phrase is stored VERBATIM, exactly as it was spoken during
    // training — its own quantity word ("ពីរ" in "យកហេននីខេនពីរកំប៉ុង") is
    // still embedded in the text. The live candidate has already had ITS
    // quantity word stripped (extractQuantity, in matchProductAndQuantity)
    // before reaching here, so comparing the two directly would almost
    // never match — training "quantity 2" against live "quantity 10" have
    // different leftover text otherwise. Stripping the trained phrase's
    // own quantity the same way (Section 4/38 — quantity is never part of
    // the trained MEANING) lets both sides reduce to the same
    // product+unit "skeleton" before comparison.
    const { textWithoutQty: trainedSkeleton } = extractQuantity(phrase);
    const s = scoreAgainstName(norm, cleanForProductMatching(normalizeText(trainedSkeleton || phrase)));
    if (s > best) best = s;
  }
  return best;
}

// trainingByProductId: optional Map<productId, string[]> of active trained
// phrases (see loadActiveVoiceTraining in voiceTrainingService.js) — pass
// an empty Map (or omit) to match on product name alone, exactly as before
// Phase 42.
// Returns { level: "confirm" | "suggest" | "none", product, candidates }.
export function matchProduct(candidateText, products, trainingByProductId = new Map()) {
  const scored = products
    .map((p) => ({ product: p, score: scoreProduct(candidateText, p, trainingByProductId.get(p.id)) }))
    .filter((s) => s.score >= 0.5)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return { level: "none", product: null, candidates: [] };

  // 0.85 is the containment tier's own score, so an exact (1.0) or
  // containment (0.85) match auto-confirms when it's not seriously
  // contested by another candidate. Token-overlap fuzzy matches are always
  // capped below 0.8 (see scoreProduct above), so they can never reach this
  // threshold on their own — a fuzzy-only match always requires the user to
  // choose, per Section 42 ("never use a low-confidence fuzzy result
  // automatically").
  const [best, second] = scored;
  if (best.score >= 0.85 && (!second || best.score - second.score >= 0.1)) {
    return { level: "confirm", product: best.product, candidates: scored.map((s) => s.product) };
  }
  return { level: "suggest", product: null, candidates: scored.map((s) => s.product) };
}

// ── Step 1 (sync, pure, no network): quantity + product only ────────────────
// Returns one of:
//   { status: "no_product", rawText }
//   { status: "ambiguous_product", rawText, candidates, qty, invalid, textWithoutQty }
//   { status: "need_unit_lookup", rawText, product, qty, invalid, textWithoutQty }
export function matchProductAndQuantity(rawText, products, trainingByProductId = new Map()) {
  const text = String(rawText || "").trim();
  if (!text) return { status: "no_product", rawText: text };

  const { qty, invalid, textWithoutQty } = extractQuantity(text);
  const match = matchProduct(textWithoutQty || text, products, trainingByProductId);
  if (match.level === "none") return { status: "no_product", rawText: text };
  if (match.level === "suggest") {
    return { status: "ambiguous_product", rawText: text, candidates: match.candidates, qty, invalid, textWithoutQty };
  }
  return { status: "need_unit_lookup", rawText: text, product: match.product, qty, invalid, textWithoutQty };
}

// ── Step 2 (sync, pure): finalize once the matched product's Active units
// are known ────────────────────────────────────────────────────────────────
// activeUnits MUST already be filtered to status === "Active" by the caller
// (Phase 38's rule: an inactive unit is never selectable, voice included —
// Section 24).
//
// Returns one of:
//   { status: "no_unit", rawText, product, activeUnits }  (product has units, none matched — let the user pick one)
//   { status: "no_quantity", rawText, product, unit }
//   { status: "invalid_quantity", rawText, product, unit }
//   { status: "ready", rawText, product, unit, qty }       (unit is null for a unit-less product)
export function resolveUnitAndFinalize({ rawText, textWithoutQty, qty, invalid, product, activeUnits }) {
  let unit = null;
  if (activeUnits.length > 0) {
    unit = matchUnitFromText(textWithoutQty || rawText, activeUnits) ||
      // Only one active unit configured — no packaging word is needed to
      // disambiguate since there is nothing to disambiguate between.
      (activeUnits.length === 1 ? activeUnits[0] : null);
    if (!unit) return { status: "no_unit", rawText, product, activeUnits };
  }

  if (invalid) return { status: "invalid_quantity", rawText, product, unit };
  if (qty == null) return { status: "no_quantity", rawText, product, unit };

  return { status: "ready", rawText, product, unit, qty };
}
